import React, { useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { layoutCSS } from "../component/common/commonStyle";
import { useGetResellerHourlyMISReportQuery } from "../store/services/reportApi";
import { useGetrcsresellermisreportQuery } from "../store/services/rcstemplateApi"; // adjust path if this hook lives in another api file

const SUPPORTED_TYPES = ["sms", "rcs"];

const ResellerMISReport = () => {
  const [serviceType, setServiceType] = useState("sms");
  const months = useMemo(
    () => [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ],
    [],
  );
  const currentYear = new Date().getFullYear();
  const [selectedMonth, setSelectedMonth] = useState(
    months[new Date().getMonth()],
  );
  const [selectedYear, setSelectedYear] = useState(currentYear);

  const years = useMemo(
    () =>
      Array.from(
        { length: currentYear + 2 - 2020 + 1 },
        (_, index) => 2020 + index,
      ),
    [currentYear],
  );

  const queryArgs = { month: selectedMonth, year: selectedYear };

  // ── SMS ──
  const {
    currentData: smsData,
    isLoading: smsLoading,
    isFetching: smsFetching,
    isError: smsError,
  } = useGetResellerHourlyMISReportQuery(queryArgs, {
    skip: serviceType !== "sms",
  });

  // ── RCS ──
  const {
    currentData: rcsData,
    isLoading: rcsLoading,
    isFetching: rcsFetching,
    isError: rcsError,
  } = useGetrcsresellermisreportQuery(queryArgs, {
    skip: serviceType !== "rcs",
  });

  // Pick the active report
  const isRcs = serviceType === "rcs";
  const data = isRcs ? rcsData : smsData;
  const isLoading = isRcs ? rcsLoading : smsLoading;
  const isFetching = isRcs ? rcsFetching : smsFetching;
  const isError = isRcs ? rcsError : smsError;

  const dataMap = useMemo(() => {
    const map = {};
    (data?.data || []).forEach(({ day, hour, count }) => {
      if (!map[day]) map[day] = {};
      map[day][hour] = count;
    });
    return map;
  }, [data]);

  const days = Array.from({ length: 31 }, (_, index) => index + 1);
  const hours = Array.from({ length: 24 }, (_, index) =>
    index < 10 ? `0${index}` : `${index}`,
  );
  const inputSx = {
    minWidth: 140,
    "& .MuiOutlinedInput-root": {
      background: "rgba(255,255,255,0.7)",
      "& fieldset": { borderColor: "#E2E8F0" },
      "&:hover fieldset": { borderColor: "#94A3B8" },
    },
    "& .MuiInputLabel-root": { color: "#475569" },
  };

  return (
    <Box sx={layoutCSS}>
      <Typography
        variant="h6"
        sx={{ mb: 2, fontSize: "0.9rem", fontWeight: "bold" }}
      >
        Reseller MIS Report
      </Typography>

      <Box
        sx={{
          display: "flex",
          gap: 1,
          flexWrap: "wrap",
          alignItems: "center",
          borderRadius: "0.5rem",
          p: 2,
          backgroundColor: "#ffffff52",
          backdropFilter: "blur(10px)",
          boxShadow: "0 4px 12px rgba(14,165,233,0.1)",
          mb: 2,
        }}
      >
        <TextField
          select
          label="Report Type"
          value={serviceType}
          onChange={(event) => setServiceType(event.target.value)}
          size="small"
          sx={inputSx}
        >
          {[
            ["sms", "SMS"],
            ["rcs", "RCS"],
            ["clicker", "Clicker"],
            ["whatsapp", "WhatsApp"],
          ].map(([value, label]) => (
            <MenuItem key={value} value={value} sx={{ fontSize: "0.75rem" }}>
              {label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Select Month"
          value={selectedMonth}
          onChange={(event) => setSelectedMonth(event.target.value)}
          size="small"
          sx={inputSx}
        >
          {months.map((month) => (
            <MenuItem key={month} value={month} sx={{ fontSize: "0.75rem" }}>
              {month}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Select Year"
          value={selectedYear}
          onChange={(event) => setSelectedYear(Number(event.target.value))}
          size="small"
          sx={inputSx}
        >
          {years.map((year) => (
            <MenuItem key={year} value={year} sx={{ fontSize: "0.75rem" }}>
              {year}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {!SUPPORTED_TYPES.includes(serviceType) ? (
        <Typography sx={{ mt: 2 }}>
          This report currently supports SMS and RCS only.
        </Typography>
      ) : isLoading || isFetching ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress size={22} />
        </Box>
      ) : isError ? (
        <Typography color="error" sx={{ mt: 2 }}>
          Failed to load reseller {serviceType.toUpperCase()} MIS report.
        </Typography>
      ) : (
        <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
          <Table
            size="small"
            sx={{
              "& .MuiTableCell-root": {
                padding: "4px 8px",
                fontSize: "0.75rem",
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell>Day</TableCell>
                {hours.map((hour) => (
                  <TableCell key={hour}>{hour}</TableCell>
                ))}
                <TableCell>Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {days.map((day) => {
                const total = Object.values(dataMap[day] || {}).reduce(
                  (sum, count) => sum + (Number(count) || 0),
                  0,
                );

                return (
                  <TableRow key={day}>
                    <TableCell>{day}</TableCell>
                    {hours.map((hour) => {
                      const count = Number(dataMap[day]?.[Number(hour)] ?? 0);
                      return (
                        <TableCell
                          key={hour}
                          sx={{
                            color: count > 0 ? "primary.main" : "inherit",
                            fontWeight: count > 0 ? "bold" : "normal",
                          }}
                        >
                          {count}
                        </TableCell>
                      );
                    })}
                    <TableCell sx={{ fontWeight: "bold" }}>{total}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default ResellerMISReport;